import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  ArrowLeft, 
  Loader2,
  CheckCircle2,
  AlertTriangle,
  Brain,
  MessageCircle,
  BookOpen,
  Heart,
  Sparkles
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ARCHETYPE_ICONS = {
  critico_interior: "⚖️",
  nino_herido: "💔",
  protector: "🛡️",
  complacedor: "🤝",
  guerrero: "⚔️",
  sabio: "📜",
  nino_libre: "✨",
  cuidador: "💚",
  perfeccionista: "🎯",
  rebelde: "🔥",
  amante: "❤️",
  ermitano: "🏔️",
  estratega: "🧠",
  sanador: "🌿",
  self: "☀️"
};

const ARCHETYPE_COLORS = {
  critico_interior: "#C62828",
  nino_herido: "#5C6BC0",
  protector: "#37474F",
  complacedor: "#F06292",
  guerrero: "#D32F2F",
  sabio: "#7E57C2",
  nino_libre: "#FFD54F",
  cuidador: "#81C784",
  perfeccionista: "#00897B",
  rebelde: "#F57C00",
  amante: "#E91E63",
  ermitano: "#78909C",
  estratega: "#5E35B1",
  sanador: "#26A69A",
  self: "#FFC107"
};

const PRACTICE_ICONS = {
  dialogo: MessageCircle,
  journaling: BookOpen,
  somatica: Heart,
  reencuadre: Brain,
  practica: Sparkles,
  ritual: Sparkles
};

export default function ArquetipoDetallePage() {
  const { arquetipoId } = useParams();
  const [arquetipo, setArquetipo] = useState(null);
  const [allArquetipos, setAllArquetipos] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [arqResponse, allResponse] = await Promise.all([
          axios.get(`${API}/arquetipo/${arquetipoId}`),
          axios.get(`${API}/arquetipos`)
        ]);
        setArquetipo(arqResponse.data);
        const arqMap = {};
        allResponse.data.forEach(a => { arqMap[a.id] = a; });
        setAllArquetipos(arqMap);
      } catch (error) {
        console.error("Error fetching arquetipo:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [arquetipoId]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="animate-spin text-primary" size={48} />
      </div>
    );
  }

  if (!arquetipo) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="font-heading text-2xl text-white mb-4">Arquetipo no encontrado</h1>
          <Link to="/biblioteca">
            <Button className="bg-primary hover:bg-primary/90 text-white rounded-full">
              Volver a la Biblioteca
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  const color = ARCHETYPE_COLORS[arquetipoId] || "#6A1B9A";
  const icon = ARCHETYPE_ICONS[arquetipoId] || "🔮";

  return (
    <div className="min-h-screen py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        {/* Back button */}
        <Link 
          to="/biblioteca" 
          className="inline-flex items-center gap-2 text-primary-300 hover:text-white transition-colors mb-8"
          data-testid="back-to-biblioteca"
        >
          <ArrowLeft size={16} />
          <span className="font-body text-sm">Volver a la Biblioteca</span>
        </Link>

        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="glass-card rounded-2xl p-8 mb-8"
          style={{ borderTop: `4px solid ${color}` }}
        >
          <div className="flex items-start gap-6">
            <span className="text-6xl">{icon}</span>
            <div className="flex-1">
              <h1 className="font-heading text-3xl sm:text-4xl text-white mb-2">
                {arquetipo.nombre}
              </h1>
              <p className="font-body text-lg text-primary-300 mb-4">
                {arquetipo.nombreIngles}
              </p>
              <p className="font-body text-primary-200 leading-relaxed">
                {arquetipo.descripcion}
              </p>
            </div>
          </div>
        </motion.div>

        {/* Tabs */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
        >
          <Tabs defaultValue="overview" className="w-full">
            <TabsList className="w-full flex flex-wrap gap-2 bg-transparent mb-8 justify-start">
              <TabsTrigger 
                value="overview" 
                className="glass-card data-[state=active]:bg-primary/30 data-[state=active]:border-primary/50 rounded-full px-4 py-2"
              >
                Visión General
              </TabsTrigger>
              <TabsTrigger 
                value="practices"
                className="glass-card data-[state=active]:bg-primary/30 data-[state=active]:border-primary/50 rounded-full px-4 py-2"
              >
                Prácticas
              </TabsTrigger>
              <TabsTrigger 
                value="relationships"
                className="glass-card data-[state=active]:bg-primary/30 data-[state=active]:border-primary/50 rounded-full px-4 py-2"
              >
                Relaciones
              </TabsTrigger>
            </TabsList>

            {/* Overview Tab */}
            <TabsContent value="overview" className="space-y-8">
              {/* Origin */}
              <div className="glass-card rounded-xl p-6">
                <h2 className="font-heading text-xl text-white mb-4">Origen</h2>
                <p className="font-body text-primary-200 leading-relaxed">
                  {arquetipo.origen}
                </p>
              </div>

              {/* Voice */}
              <div className="glass-card rounded-xl p-6">
                <h2 className="font-heading text-xl text-white mb-4">Su Voz</h2>
                <p className="font-body text-sm text-primary-300 mb-4">
                  Frases típicas que este arquetipo "dice" en tu mente:
                </p>
                <div className="flex flex-wrap gap-2">
                  {arquetipo.fraseTipica?.map((frase, i) => (
                    <span 
                      key={i}
                      className="bg-primary-950/50 text-primary-200 px-4 py-2 rounded-lg text-sm font-body italic border border-white/5"
                    >
                      "{frase}"
                    </span>
                  ))}
                </div>
              </div>

              {/* Gifts & Shadows */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div className="glass-card rounded-xl p-6" style={{ borderLeft: `4px solid ${color}` }}>
                  <h2 className="font-heading text-xl text-secondary mb-4 flex items-center gap-2">
                    <CheckCircle2 size={20} />
                    Dones (Equilibrado)
                  </h2>
                  <ul className="space-y-3">
                    {arquetipo.dones?.map((don, i) => (
                      <li key={i} className="flex items-start gap-3 text-primary-200 font-body">
                        <CheckCircle2 className="text-secondary shrink-0 mt-1" size={16} />
                        {don}
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="glass-card rounded-xl p-6 border-l-4 border-accent">
                  <h2 className="font-heading text-xl text-accent-glow mb-4 flex items-center gap-2">
                    <AlertTriangle size={20} />
                    Sombras (Desbalanceado)
                  </h2>
                  <ul className="space-y-3">
                    {arquetipo.sombras?.map((sombra, i) => (
                      <li key={i} className="flex items-start gap-3 text-primary-200 font-body">
                        <AlertTriangle className="text-accent-glow shrink-0 mt-1" size={16} />
                        {sombra}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              {/* Neuroscience */}
              <div className="glass-card rounded-xl p-6 bg-primary-950/50">
                <h2 className="font-heading text-xl text-primary-300 mb-4 flex items-center gap-2">
                  <Brain size={20} />
                  Neurociencia
                </h2>
                <p className="font-body text-primary-200 leading-relaxed">
                  {arquetipo.neurociencia}
                </p>
              </div>
            </TabsContent>

            {/* Practices Tab */}
            <TabsContent value="practices" className="space-y-6">
              <div className="glass-card rounded-xl p-6 mb-6 bg-secondary/5 border border-secondary/20">
                <h2 className="font-heading text-xl text-secondary mb-2">
                  Armonizando a {arquetipo.nombre}
                </h2>
                <p className="font-body text-sm text-primary-300">
                  Estas prácticas te ayudarán a desarrollar una relación más saludable con este arquetipo, 
                  honrando sus dones mientras integras sus sombras.
                </p>
              </div>

              {arquetipo.practicasArmonizacion?.map((practica, index) => {
                const Icon = PRACTICE_ICONS[practica.tipo] || Sparkles;
                return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="glass-card rounded-xl p-6"
                  >
                    <div className="flex items-start gap-4">
                      <div 
                        className="p-3 rounded-full shrink-0"
                        style={{ backgroundColor: `${color}20`, color: color }}
                      >
                        <Icon size={24} />
                      </div>
                      <div>
                        <span className="text-xs font-body text-primary-400 uppercase tracking-wide">
                          {practica.tipo}
                        </span>
                        <h3 className="font-heading text-xl text-white mt-1 mb-3">
                          {practica.titulo}
                        </h3>
                        <p className="font-body text-primary-200 leading-relaxed whitespace-pre-line">
                          {practica.descripcion}
                        </p>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </TabsContent>

            {/* Relationships Tab */}
            <TabsContent value="relationships" className="space-y-8">
              {/* Conflicts */}
              {arquetipo.relacionesTipicas?.conflictoCon?.length > 0 && (
                <div className="glass-card rounded-xl p-6">
                  <h2 className="font-heading text-xl text-accent-glow mb-4">
                    Conflictos Típicos Con
                  </h2>
                  <p className="font-body text-sm text-primary-300 mb-4">
                    {arquetipo.nombre} suele tener tensiones con estos arquetipos:
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {arquetipo.relacionesTipicas.conflictoCon.map((relId) => {
                      const relArq = allArquetipos[relId];
                      if (!relArq) return null;
                      return (
                        <Link
                          key={relId}
                          to={`/arquetipo/${relId}`}
                          className="flex items-center gap-3 p-3 rounded-lg bg-accent/5 border border-accent/20 hover:border-accent/40 transition-colors"
                        >
                          <span className="text-2xl">{ARCHETYPE_ICONS[relId]}</span>
                          <div>
                            <p className="font-body text-white">{relArq.nombre}</p>
                            <p className="font-body text-xs text-primary-400">{relArq.nombreIngles}</p>
                          </div>
                        </Link>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Alliances */}
              {arquetipo.relacionesTipicas?.alianzaCon?.length > 0 && (
                <div className="glass-card rounded-xl p-6">
                  <h2 className="font-heading text-xl text-secondary mb-4">
                    Alianzas Típicas Con
                  </h2>
                  <p className="font-body text-sm text-primary-300 mb-4">
                    {arquetipo.nombre} suele formar coaliciones con estos arquetipos:
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {arquetipo.relacionesTipicas.alianzaCon.map((relId) => {
                      const relArq = allArquetipos[relId];
                      if (!relArq) return null;
                      return (
                        <Link
                          key={relId}
                          to={`/arquetipo/${relId}`}
                          className="flex items-center gap-3 p-3 rounded-lg bg-secondary/5 border border-secondary/20 hover:border-secondary/40 transition-colors"
                        >
                          <span className="text-2xl">{ARCHETYPE_ICONS[relId]}</span>
                          <div>
                            <p className="font-body text-white">{relArq.nombre}</p>
                            <p className="font-body text-xs text-primary-400">{relArq.nombreIngles}</p>
                          </div>
                        </Link>
                      );
                    })}
                  </div>
                </div>
              )}
            </TabsContent>
          </Tabs>
        </motion.div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4 }}
          className="glass-card rounded-2xl p-8 mt-12 text-center"
        >
          <h2 className="font-heading text-2xl text-white mb-4">
            ¿Quieres descubrir qué tan activo está este arquetipo en ti?
          </h2>
          <p className="font-body text-primary-300 mb-6">
            Realiza el test completo para conocer tu ecosistema de arquetipos internos.
          </p>
          <Link to="/test" data-testid="cta-test-from-archetype">
            <Button className="bg-primary hover:bg-primary/90 text-white rounded-full px-8 py-3 shadow-glow-primary">
              <Sparkles className="mr-2" size={18} />
              Hacer el Test
            </Button>
          </Link>
        </motion.div>
      </div>
    </div>
  );
}
